'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId, MarketId } from '@morpho-org/blue-sdk';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { Button } from '@repo/ui';
import { ArrowDiagonalUp, Close } from '@repo/ui/icons';
import { lazy } from 'react';
import { AUTO_DELEVERAGE_DOCS_URL } from '@/config';
import { useChainNavigation } from '@/hooks/useChainNavigation';
import { trackAdlSunsetNudgeDisableClicked, trackAdlSunsetNudgeGotItClicked, trackAdlSunsetNudgeLearnMoreClicked } from '@/lib/analytics/client/trackers/adl-sunset-nudge';

// Lazy: only the market variant opens this on "Disable", and it pulls in the
// full Auto-Deleverage transaction flow (simulation provider, market query,
// review dialog, viem/bundler code). Loading it lazily keeps that code out of
// this module's chunk so the dashboard variant, which only renders "Got it",
// doesn't pay for it. Matches the lazy-dialog pattern in DialogOnMountData.
const AutoDeleverageDialog = lazy(() => import('@/components/common/AutoDeleverage/AutoDeleverageDialog/AutoDeleverageDialog'));

/**
 * Dashboard variant nudges across all of a user's positions and points to the
 * positions list below. Market variant targets a single market and offers a
 * direct "Disable" action, so it carries the market context.
 */
type AdlSunsetNudgeDialogProps = {
  source: 'dashboard';
} | {
  source: 'market_page';
  chainId: ChainId;
  marketId: MarketId;
};
const AdlSunsetNudgeDialog = props => {
  const $ = _c(31);
  const {
    toggleDialog
  } = useDialogContext();
  let t0;
  if ($[0] !== toggleDialog) {
    t0 = () => toggleDialog(false);
    $[0] = toggleDialog;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const close = t0;
  let t1;
  if ($[2] !== props.chainId || $[3] !== props.marketId || $[4] !== props.source) {
    t1 = () => {
      trackAdlSunsetNudgeLearnMoreClicked(props.source === "market_page" ? {
        source: "market_page",
        chainId: props.chainId,
        marketId: props.marketId
      } : {
        source: "dashboard"
      });
    };
    $[2] = props.chainId;
    $[3] = props.marketId;
    $[4] = props.source;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleLearnMore = t1;
  const body = props.source === "market_page" ? "You have Auto-Deleverage enabled on this market. The feature is being sunset and is no longer accepting new positions. We recommend disabling it." : "You have at least one borrow position with Auto-Deleverage enabled. The feature is being sunset and is no longer accepting new positions. We recommend disabling it on the affected position(s) below.";
  let t2;
  if ($[6] !== close) {
    t2 = () => {
      trackAdlSunsetNudgeGotItClicked();
      close();
    };
    $[6] = close;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleGotIt = t2;
  let t3;
  if ($[8] !== handleGotIt || $[9] !== props.chainId || $[10] !== props.marketId || $[11] !== props.source) {
    t3 = () => {
      if (props.source !== "market_page") {
        return <Button onClick={handleGotIt} className="max-md:w-full">Got it</Button>;
      }
      return <MarketDisableAction chainId={props.chainId} marketId={props.marketId} />;
    };
    $[8] = handleGotIt;
    $[9] = props.chainId;
    $[10] = props.marketId;
    $[11] = props.source;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const renderPrimaryAction = t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Close />;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== close) {
    t5 = <Button size="icon" onClick={close} variant="secondary" className="top-md right-md absolute" aria-label="Close">{t4}</Button>;
    $[14] = close;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h2 className="text-title-xs text-center">Auto-Deleverage is being sunset</h2>;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== body) {
    t7 = <div className="gap-sm flex flex-col items-center px-[40px]">{t6}<p className="text-secondary-foreground text-center text-xs text-balance">{body}</p></div>;
    $[17] = body;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== renderPrimaryAction) {
    t8 = renderPrimaryAction();
    $[19] = renderPrimaryAction;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ArrowDiagonalUp />;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== handleLearnMore) {
    t10 = <ExternalLink href={AUTO_DELEVERAGE_DOCS_URL}><Button variant="ghost" onClick={handleLearnMore}>Learn more{t9}</Button></ExternalLink>;
    $[22] = handleLearnMore;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8) {
    t11 = <div className="gap-sm flex flex-col items-center">{t8}{t10}</div>;
    $[24] = t10;
    $[25] = t8;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t5 || $[29] !== t7) {
    t12 = <div className="gap-lg flex flex-col items-center">{t5}{t7}{t11}</div>;
    $[27] = t11;
    $[28] = t5;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
};

/**
 * Market-variant primary action. Split out so `useChainNavigation` can be
 * called unconditionally with a guaranteed `chainId` (the dashboard variant
 * has no market context).
 */
const MarketDisableAction = t0 => {
  const $ = _c(7);
  const {
    chainId,
    marketId
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  let t1;
  if ($[0] !== chainId) {
    t1 = {
      chainId
    };
    $[0] = chainId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    syncChainSwitch
  } = useChainNavigation(t1);
  let t2;
  if ($[2] !== chainId || $[3] !== marketId || $[4] !== syncChainSwitch || $[5] !== toggleDialog) {
    const openDisableDialog = () => {
      trackAdlSunsetNudgeDisableClicked({
        chainId,
        marketId
      });
      syncChainSwitch();
      toggleDialog(<AutoDeleverageDialog marketId={marketId} chainId={chainId} toEnable={false} />);
    };
    t2 = <Button onClick={openDisableDialog} className="max-md:w-full">Disable Auto-Deleverage</Button>;
    $[2] = chainId;
    $[3] = marketId;
    $[4] = syncChainSwitch;
    $[5] = toggleDialog;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export default AdlSunsetNudgeDialog;